import { useState, useCallback, useEffect } from "react";
import { useUrlSyncedState } from "./useUrlSyncedState";
import {
  convertToUtcPreserveTime,
  convertFromUtcPreserveTime,
} from "../../../shared/helpers/datetime";
import { AssignmentFilterParams } from "@/shared/types/filters";

export const FILTER_KEYS = {
  SEARCH: "search",
  ORDER_SOURCE: "order_source",
  ANNOTATOR_ID: "annotator_id",
  FROM_DATETIME: "from_datetime",
  TO_DATETIME: "to_datetime",
  ORDERING: "ordering",
  STAGE_JOURNEY_ANNOTATION: "stage_journey_annotation",
  STAGE_TRANSACTION_ANNOTATION: "stage_transaction_annotation",
  STAGE_TRACK_PERSISTENCE_ANNOTATION: "stage_track_persistence_annotation",
} as const;

export type FilterKey = (typeof FILTER_KEYS)[keyof typeof FILTER_KEYS];

export function useAssignmentFilters(options: {
  onApplyFilters?: () => void;
  onClearFilters?: () => void;
}) {
  const { getParam, updateParams } = useUrlSyncedState();

  const [orderId, setOrderId] = useState(getParam(FILTER_KEYS.SEARCH) || "");
  const [source, setSource] = useState(
    getParam(FILTER_KEYS.ORDER_SOURCE) || "all"
  );
  const [assignee, setAssignee] = useState(
    getParam(FILTER_KEYS.ANNOTATOR_ID) || "all"
  );
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [stageJourneyAnnotation, setStageJourneyAnnotation] = useState(
    getParam(FILTER_KEYS.STAGE_JOURNEY_ANNOTATION) || "all"
  );
  const [stageTransactionAnnotation, setStageTransactionAnnotation] = useState(
    getParam(FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION) || "all"
  );
  const [stageTrackPersistenceAnnotation, setStageTrackPersistenceAnnotation] =
    useState(getParam(FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION) || "all");
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasAssignmentOccurred, setHasAssignmentOccurred] = useState(false);

  const [activeFilters, setActiveFilters] = useState<AssignmentFilterParams>(
    {}
  );

  const parseDateParam = useCallback((dateString: string | null) => {
    return dateString ? convertFromUtcPreserveTime(dateString) : null;
  }, []);

  const initializeFiltersFromUrl = useCallback(() => {
    const paramValues = {
      search: getParam(FILTER_KEYS.SEARCH),
      orderSource: getParam(FILTER_KEYS.ORDER_SOURCE),
      annotatorId: getParam(FILTER_KEYS.ANNOTATOR_ID),
      fromDatetime: getParam(FILTER_KEYS.FROM_DATETIME),
      toDatetime: getParam(FILTER_KEYS.TO_DATETIME),
      ordering: getParam(FILTER_KEYS.ORDERING),
      stageJourneyAnnotation: getParam(FILTER_KEYS.STAGE_JOURNEY_ANNOTATION),
      stageTransactionAnnotation: getParam(
        FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION
      ),
      stageTrackPersistenceAnnotation: getParam(
        FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION
      ),
    };

    if (paramValues.fromDatetime) {
      setFromDate(parseDateParam(paramValues.fromDatetime));
    }

    if (paramValues.toDatetime) {
      setToDate(parseDateParam(paramValues.toDatetime));
    }

    const initialFilters: AssignmentFilterParams = {};

    if (paramValues.search) {
      initialFilters[FILTER_KEYS.SEARCH] = paramValues.search;
    }

    if (paramValues.orderSource && paramValues.orderSource !== "all") {
      initialFilters[FILTER_KEYS.ORDER_SOURCE] = paramValues.orderSource;
    }

    if (paramValues.annotatorId && paramValues.annotatorId !== "all") {
      initialFilters[FILTER_KEYS.ANNOTATOR_ID] = paramValues.annotatorId;
    }

    if (paramValues.fromDatetime) {
      initialFilters[FILTER_KEYS.FROM_DATETIME] = paramValues.fromDatetime;
    }

    if (paramValues.toDatetime) {
      initialFilters[FILTER_KEYS.TO_DATETIME] = paramValues.toDatetime;
    }

    if (paramValues.ordering) {
      initialFilters[FILTER_KEYS.ORDERING] = paramValues.ordering;
    } else {
      initialFilters[FILTER_KEYS.ORDERING] = "-time";
      updateParams({ [FILTER_KEYS.ORDERING]: "-time" });
    }

    if (paramValues.stageJourneyAnnotation) {
      initialFilters[FILTER_KEYS.STAGE_JOURNEY_ANNOTATION] =
        paramValues.stageJourneyAnnotation;
    }

    if (paramValues.stageTransactionAnnotation) {
      initialFilters[FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION] =
        paramValues.stageTransactionAnnotation;
    }

    if (paramValues.stageTrackPersistenceAnnotation) {
      initialFilters[FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION] =
        paramValues.stageTrackPersistenceAnnotation;
    }

    setActiveFilters(initialFilters);
    const filtersWithoutOrdering = { ...initialFilters };
    delete filtersWithoutOrdering[FILTER_KEYS.ORDERING];
    setIsFiltersApplied(Object.keys(filtersWithoutOrdering).length > 0);
    setIsInitialized(true);
  }, [getParam, parseDateParam, updateParams]);

  useEffect(() => {
    initializeFiltersFromUrl();
  }, [initializeFiltersFromUrl]);

  const onOrderIdChange = useCallback((value: string) => {
    setOrderId(value);
    setIsFiltersApplied(false);
  }, []);

  const onSourceChange = useCallback((value: string) => {
    setSource(value);
    setIsFiltersApplied(false);
  }, []);

  const onAssigneeChange = useCallback((value: string) => {
    setAssignee(value);
    setIsFiltersApplied(false);
  }, []);

  const onFromDateChange = useCallback((date: Date | null) => {
    setFromDate(date);
    setIsFiltersApplied(false);
  }, []);

  const onToDateChange = useCallback((date: Date | null) => {
    setToDate(date);
    setIsFiltersApplied(false);
  }, []);

  const onStageJourneyAnnotationChange = useCallback((value: string) => {
    setStageJourneyAnnotation(value);
    setIsFiltersApplied(false);
  }, []);

  const onStageTransactionAnnotationChange = useCallback((value: string) => {
    setStageTransactionAnnotation(value);
    setIsFiltersApplied(false);
  }, []);

  const onStageTrackPersistenceAnnotationChange = useCallback(
    (value: string) => {
      setStageTrackPersistenceAnnotation(value);
      setIsFiltersApplied(false);
    },
    []
  );

  const applyFilters = useCallback(() => {
    const filters: AssignmentFilterParams = {
      [FILTER_KEYS.SEARCH]: orderId || undefined,
      [FILTER_KEYS.ORDER_SOURCE]: source !== "all" ? source : undefined,
      [FILTER_KEYS.ANNOTATOR_ID]:
        assignee !== "all"
          ? assignee === "null"
            ? "null"
            : assignee
          : undefined,
      [FILTER_KEYS.FROM_DATETIME]: fromDate
        ? convertToUtcPreserveTime(fromDate)
        : undefined,
      [FILTER_KEYS.TO_DATETIME]: toDate
        ? convertToUtcPreserveTime(toDate)
        : undefined,
      [FILTER_KEYS.ORDERING]: activeFilters.ordering,
      [FILTER_KEYS.STAGE_JOURNEY_ANNOTATION]:
        stageJourneyAnnotation !== "all" ? stageJourneyAnnotation : undefined,
      [FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION]:
        stageTransactionAnnotation !== "all"
          ? stageTransactionAnnotation
          : undefined,
      [FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION]:
        stageTrackPersistenceAnnotation !== "all"
          ? stageTrackPersistenceAnnotation
          : undefined,
    };

    setActiveFilters(filters);
    setIsFiltersApplied(true);

    options.onApplyFilters?.();
    updateParams(filters);

    return filters;
  }, [
    orderId,
    source,
    assignee,
    fromDate,
    toDate,
    activeFilters.ordering,
    stageJourneyAnnotation,
    stageTransactionAnnotation,
    stageTrackPersistenceAnnotation,
    updateParams,
    options,
  ]);

  const clearFilters = useCallback(() => {
    setOrderId("");
    setSource("all");
    setAssignee("all");
    setFromDate(null);
    setToDate(null);
    setStageJourneyAnnotation("all");
    setStageTransactionAnnotation("all");
    setStageTrackPersistenceAnnotation("all");

    const newFilters: AssignmentFilterParams = {};
    if (activeFilters.ordering) {
      newFilters[FILTER_KEYS.ORDERING] = activeFilters.ordering;
    }

    setActiveFilters(newFilters);
    setIsFiltersApplied(Object.keys(newFilters).length > 0);

    if (options.onClearFilters) {
      options.onClearFilters();
    }

    const paramsToClear = [
      FILTER_KEYS.SEARCH,
      FILTER_KEYS.ORDER_SOURCE,
      FILTER_KEYS.ANNOTATOR_ID,
      FILTER_KEYS.FROM_DATETIME,
      FILTER_KEYS.TO_DATETIME,
      FILTER_KEYS.STAGE_JOURNEY_ANNOTATION,
      FILTER_KEYS.STAGE_TRANSACTION_ANNOTATION,
      FILTER_KEYS.STAGE_TRACK_PERSISTENCE_ANNOTATION,
    ];

    const paramsToUpdate = paramsToClear.reduce(
      (acc, key) => {
        acc[key] = null;
        return acc;
      },
      {} as Record<string, null | string>
    );

    if (activeFilters.ordering) {
      paramsToUpdate[FILTER_KEYS.ORDERING] = activeFilters.ordering;
    }

    updateParams(paramsToUpdate);

    return newFilters;
  }, [activeFilters.ordering, updateParams, options]);

  const updateOrdering = useCallback(
    (ordering: string) => {
      setActiveFilters((prevFilters) => {
        const updatedFilters = { ...prevFilters };

        if (ordering) {
          updatedFilters[FILTER_KEYS.ORDERING] = ordering;
        } else {
          delete updatedFilters[FILTER_KEYS.ORDERING];
        }

        return updatedFilters;
      });

      updateParams({ [FILTER_KEYS.ORDERING]: ordering || null });
    },
    [updateParams]
  );

  const clearOrdering = useCallback(() => {
    setActiveFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      delete updatedFilters[FILTER_KEYS.ORDERING];
      return updatedFilters;
    });

    updateParams({ [FILTER_KEYS.ORDERING]: null });
  }, [updateParams]);

  const markAssignmentOccurred = useCallback(() => {
    setHasAssignmentOccurred(true);
    setIsFiltersApplied(false);
  }, []);

  const resetAssignmentState = useCallback(() => {
    setHasAssignmentOccurred(false);
  }, []);

  return {
    filterState: {
      orderId,
      source,
      assignee,
      fromDate,
      toDate,
      isFiltersApplied,
      isInitialized,
      hasAssignmentOccurred,
      stageJourneyAnnotation,
      stageTransactionAnnotation,
      stageTrackPersistenceAnnotation,
    },

    filterHandlers: {
      onOrderIdChange,
      onSourceChange,
      onAssigneeChange,
      onFromDateChange,
      onToDateChange,
      onStageJourneyAnnotationChange,
      onStageTransactionAnnotationChange,
      onStageTrackPersistenceAnnotationChange,
      applyFilters,
      clearFilters,
      updateOrdering,
      clearOrdering,
      markAssignmentOccurred,
      resetAssignmentState,
    },

    activeFilters,
  };
}
